import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    required,
    useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import {
    REPORT_ACTION_CHOICES,
    REPORT_DECISION_CHOICES,
    REPORT_ROOT_CAUSE_CHOICES,
    REPORT_STATUS_CHOICES,
} from './constants';
import { AiReportRecord } from './types';

const NEXT_STATUSES_BY_CURRENT: Record<string, string[]> = {
    NEW: ['TRIAGE', 'IN_REVIEW', 'ACTIONED', 'CLOSED'],
    TRIAGE: ['IN_REVIEW', 'ACTIONED', 'CLOSED'],
    IN_REVIEW: ['ACTIONED', 'CLOSED', 'REOPENED'],
    ACTIONED: ['CLOSED', 'REOPENED'],
    CLOSED: ['REOPENED'],
    REOPENED: ['TRIAGE', 'IN_REVIEW', 'ACTIONED', 'CLOSED'],
};

const STATUSES_BY_ACTION_TYPE: Record<string, string[]> = {
    MARK_VALID: ['IN_REVIEW', 'ACTIONED', 'CLOSED'],
    MARK_INVALID: ['CLOSED'],
    ESCALATE: ['TRIAGE', 'IN_REVIEW'],
    CLOSE: ['CLOSED'],
    REOPEN: ['REOPENED'],
    APPLY_POLICY_FIX: ['IN_REVIEW', 'ACTIONED'],
    REQUEST_MODEL_ROLLBACK: ['TRIAGE', 'ACTIONED'],
};

const ReadonlySummary = () => {
    const record = useRecordContext<AiReportRecord>();
    if (!record) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                Report Summary
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={`Status: ${record.status}`} color="primary" variant="outlined" size="small" />
                <Chip label={`Risk: ${record.risk_level}`} color="warning" variant="outlined" size="small" />
                <Chip label={`Reason: ${record.reason_code}`} size="small" />
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {record.reported_message_text}
            </Typography>
        </Box>
    );
};

const ContextSnapshots = () => {
    const record = useRecordContext<AiReportRecord>();
    const snapshots = Array.isArray(record?.snapshots) ? record.snapshots : [];

    if (!record) return null;

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Context Snapshots
            </Typography>
            {snapshots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No snapshots
                </Typography>
            ) : (
                snapshots.map((snapshot, index) => (
                    <Box key={`${snapshot.seq || index}-${snapshot.role || 'UNKNOWN'}`} sx={{ mb: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {`${snapshot.role || 'UNKNOWN'} | seq ${snapshot.seq || 0} | ${snapshot.message_timestamp || ''}`}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {String(snapshot.content || '')}
                        </Typography>
                    </Box>
                ))
            )}
        </Box>
    );
};

const ModerationActions = () => {
    const record = useRecordContext<AiReportRecord>();
    const actions = Array.isArray(record?.actions) ? record.actions : [];

    if (!record) return null;

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Action History
            </Typography>
            {actions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No actions
                </Typography>
            ) : (
                actions.map((action, index) => (
                    <Box key={`${action.created_at || index}-${action.action_type || 'ACTION'}`} sx={{ mb: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {`${action.created_at || ''} | ${action.action_type || ''} | ${action.from_status || '-'} -> ${action.to_status || '-'}`}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {String(action.note || '')}
                        </Typography>
                    </Box>
                ))
            )}
        </Box>
    );
};

const QuickActionPresets = () => {
    const record = useRecordContext<AiReportRecord>();
    const { setValue } = useFormContext();

    const allowedStatuses = useMemo(() => {
        const fromWorkflow = record?.workflow?.allowedNextStatuses;
        const current = record?.workflow?.currentStatus || record?.status;
        if (Array.isArray(fromWorkflow) && fromWorkflow.length > 0) {
            return record?.workflow?.allowNoopTransition
                ? Array.from(new Set([...(fromWorkflow || []), current || '']))
                : fromWorkflow;
        }
        const fallback = current ? NEXT_STATUSES_BY_CURRENT[current] || [] : [];
        return Array.from(new Set([...fallback, current || '']));
    }, [record?.status, record?.workflow]);

    const canApply = (toStatus: string) => allowedStatuses.includes(toStatus);

    const applyPreset = (
        actionType: string,
        toStatus: string,
        decision?: string,
        note?: string
    ) => {
        setValue('actionType', actionType);
        setValue('toStatus', toStatus);
        setValue('decision', decision || '');
        if (note) {
            setValue('note', note);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Quick Actions
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset('ESCALATE', 'TRIAGE', 'NEED_MORE_INFO', 'Escalated for triage review')}
                    disabled={!canApply('TRIAGE')}
                >
                    Escalate To Triage
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset('MARK_VALID', 'IN_REVIEW', 'CONFIRMED', 'Marked valid and moved to review')}
                    disabled={!canApply('IN_REVIEW')}
                >
                    Mark Valid
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset('CLOSE', 'CLOSED', 'REJECTED', 'Closed as rejected')}
                    disabled={!canApply('CLOSED')}
                >
                    Reject And Close
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset('REOPEN', 'REOPENED', 'NEED_MORE_INFO', 'Reopened for follow-up')}
                    disabled={!canApply('REOPENED')}
                >
                    Reopen
                </Button>
            </Stack>
        </Box>
    );
};

const ToStatusInput = () => {
    const record = useRecordContext<AiReportRecord>();
    const { setValue } = useFormContext();
    const actionType = useWatch({ name: 'actionType' }) as string | undefined;
    const toStatus = useWatch({ name: 'toStatus' }) as string | undefined;

    const current = record?.workflow?.currentStatus || record?.status || '';
    const allowedByWorkflow = useMemo(() => {
        const fromWorkflow = record?.workflow?.allowedNextStatuses;
        if (Array.isArray(fromWorkflow) && fromWorkflow.length > 0) {
            return record?.workflow?.allowNoopTransition
                ? Array.from(new Set([...(fromWorkflow || []), current]))
                : fromWorkflow;
        }
        const fallback = NEXT_STATUSES_BY_CURRENT[current] || [];
        return Array.from(new Set([...fallback, current]));
    }, [current, record?.workflow]);

    const allowed = useMemo(() => {
        if (!actionType) {
            return allowedByWorkflow;
        }
        const allowedByAction = STATUSES_BY_ACTION_TYPE[actionType] || [];
        return allowedByWorkflow.filter(status => allowedByAction.includes(status));
    }, [actionType, allowedByWorkflow]);

    const allowedKey = allowed.join('|');

    useEffect(() => {
        if (!toStatus) {
            if (allowed.length === 1) {
                setValue('toStatus', allowed[0]);
            }
            return;
        }

        if (!allowed.includes(toStatus)) {
            setValue('toStatus', allowed.length === 1 ? allowed[0] : '');
        }
    }, [allowedKey, allowed, setValue, toStatus]);

    const choices = REPORT_STATUS_CHOICES.filter(choice => allowed.includes(String(choice.id)));
    const helperText = current
        ? `Current: ${current}. Allowed: ${allowed.join(', ') || 'None for current action type'}`
        : undefined;

    return (
        <SelectInput
            source="toStatus"
            label="To Status"
            choices={choices}
            validate={required()}
            helperText={helperText}
            disabled={choices.length === 0}
            fullWidth
        />
    );
};

const AiReportEdit = () => {
    return (
        <Edit>
            <SimpleForm>
                <ReadonlySummary />

                <TextInput source="report_id" label="Report ID" disabled fullWidth />
                <TextInput source="user_id" label="User ID" disabled fullWidth />
                <TextInput source="conversation_id" label="Conversation ID" disabled fullWidth />
                <TextInput source="message_id" label="Message ID" disabled fullWidth />
                <TextInput source="reason_code" label="Reason" disabled fullWidth />
                <TextInput source="reason_detail" label="Reason Detail" disabled fullWidth multiline minRows={2} />
                <TextInput source="model_version" label="Model Version" disabled fullWidth />
                <TextInput source="created_at" label="Created At" disabled fullWidth />
                <TextInput source="reported_message_text" label="Reported Message" disabled fullWidth multiline minRows={4} />

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">Submit Admin Action</Typography>
                <QuickActionPresets />

                <SelectInput
                    source="actionType"
                    label="Action Type"
                    choices={REPORT_ACTION_CHOICES}
                    validate={required()}
                    fullWidth
                />
                <ToStatusInput />
                <SelectInput
                    source="decision"
                    label="Decision"
                    choices={REPORT_DECISION_CHOICES}
                    emptyText="(Optional)"
                    fullWidth
                />
                <SelectInput
                    source="rootCause"
                    label="Root Cause"
                    choices={REPORT_ROOT_CAUSE_CHOICES}
                    emptyText="(Optional)"
                    fullWidth
                />
                <TextInput
                    source="tags"
                    label="Tags"
                    helperText="Comma separated, e.g. policy,coach,urgent"
                    fullWidth
                />
                <TextInput source="note" label="Action Note" fullWidth multiline minRows={3} />

                <ContextSnapshots />
                <ModerationActions />
            </SimpleForm>
        </Edit>
    );
};

export default AiReportEdit;
